import React, { useCallback, useEffect, useState } from "react";

interface BarData {
  label: string;
  color: string;
  value: number;
}

interface Dataset {
  label: string;
  data: BarData[];
}

interface SimpleBarGraphProps {
  id: string;
  datasets: Dataset[];
  gap: number;
  barSize: number;
  maxSize?: number;
  graphSize?: number;
  barBorderRadius?: string;
  hasSubsections?: boolean;
  barTotal?: number;
  onGraphClick?: () => void;
}

const TutorBarGraph: React.FC<SimpleBarGraphProps> = ({
  id,
  gap,
  datasets,
  barSize,
  maxSize,
  graphSize,
  barBorderRadius = "0",
  hasSubsections,
  barTotal,
  onGraphClick,
}) => {
  const [mapData, setMapData] = useState<Dataset[]>([]);
  if (datasets.length === 0) {
    return null;
  }
  const graphDimValue = graphSize !== undefined ? `${graphSize}rem` : "100%";

  useEffect(() => {
    if (datasets.length > 0) {
      const newMapData = structuredClone(datasets) as Dataset[];
      const lastMapData = newMapData[newMapData.length - 1];

      newMapData[newMapData.length - 1] = {
        ...lastMapData,
        data: lastMapData.data.reduce((acc, cur) => {
          if (cur.value === 0 && acc.length > 0) {
            const prevSection = acc[acc.length - 1];
            if (prevSection.value === 0) {
              prevSection.label = `${cur.label},${prevSection.label.replace(cur.label.split(" ")[0], "")}`;
            } else {
              acc.push(cur);
            }
          } else {
            acc.push(cur);
          }

          return acc;
        }, [] as BarData[]),
      };

      setMapData(newMapData);
    }
  }, [datasets]);

  const renderLeftColumnLabels = useCallback(
    () => (
      <div className="d-flex flex-column flex-nowrap justify-content-between">
        {mapData &&
          mapData.map((bar, index) => {
            const tick = bar.label;
            return (
              <div
                key={`${tick}-${index}-label`}
                className="sbg-tick sgb-tick-horizontal d-flex flex-column justify-content-center"
                style={{
                  height: `${barSize}rem`,
                }}
              >
                {tick}
              </div>
            );
          })}
      </div>
    ),
    [mapData, barSize]
  );

  const renderBarSubsectionLabels = useCallback(
    (lastBar: boolean, percentFloatValue: number, secLabel: string) => {
      if (hasSubsections && lastBar) {
        return (
          <span
            className={`sbg-label sbg-label-horizontal ${percentFloatValue <= 0.1 && percentFloatValue !== 0 ? "sbg-label-extended" : ""}`}
          >
            {secLabel}
            <div className="sbg-label-line"></div>
          </span>
        );
      } else if (!hasSubsections) {
        return (
          <>
            <span className="sbg-single-bar-label sbg-label sbg-label-horizontal">
              {secLabel}

              <div className="sbg-label-line"></div>
            </span>
          </>
        );
      } else {
        return null;
      }
    },
    [mapData, hasSubsections]
  );

  const calculateBarBorders = useCallback(
    (subIndex: number, bar: Dataset): string => {
      let topRadius = `0 0`;
      let botRadius = `0 0`;
      if (subIndex === 0 || (subIndex === 1 && bar.data[0].value === 0)) {
        botRadius = `${barBorderRadius} ${barBorderRadius}`;
      }
      if (
        subIndex === bar.data.length - 1 ||
        (subIndex === bar.data.length - 2 &&
          bar.data[bar.data.length - 1].value === 0)
      ) {
        topRadius = `${barBorderRadius} ${barBorderRadius}`;
      }
      const borderRadius = `${botRadius.split(" ")[0]} ${topRadius.split(" ")[0]} ${topRadius.split(" ")[1]} ${botRadius.split(" ")[1]}`;

      return borderRadius;
    },
    [mapData]
  );

  const barClasses = hasSubsections
    ? "sbg-bar-graph-horizontal"
    : "sbg-simple-bar-graph-horizontal";

  return (
    <div
      className={`sbg-bar-graph flex-column ${barClasses} ${graphDimValue === "100%" ? "flex-grow-1" : ""}`}
      data-testid={`${id}-bar-graph`}
    >
      <div
        className="d-flex flex-nowrap position-relative"
        style={{
          cursor: onGraphClick ? "pointer" : "default",
        }}
        onClick={onGraphClick}
      >
        {renderLeftColumnLabels()}
        <div
          className={`d-flex flex-column flex-nowrap ${maxSize === undefined ? "align-items-end" : ""} gap-${gap}`}
          style={{
            width: `${graphDimValue}`,
          }}
        >
          {mapData &&
            mapData.map((bar, index) => {
              const tick = bar.label;
              const total = bar.data.reduce((acc, b) => acc + b.value, 0);
              const isLastBar = index === mapData.length - 1;

              return (
                <div
                  key={`${tick}-${index}-bar`}
                  data-testid={`${id}-bar-${index}`}
                  className="d-flex flex-row sgb-bar-container-horizontal flex-nowrap align-items-center"
                  style={{
                    width: `${graphDimValue}`,
                    maxWidth: `${maxSize + "rem"}`,
                  }}
                >
                  {bar.data.map((section, subIndex) => {
                    const {
                      color: secColor,
                      value: secVal,
                      label: secLabel,
                    } = section;

                    const percentFloatValue =
                      secVal / (barTotal !== undefined ? barTotal : total);

                    let legendDisplay = renderBarSubsectionLabels(
                      isLastBar,
                      percentFloatValue,
                      secLabel
                    );

                    if (secVal === 0) {
                      return (
                        <div
                          key={`${tick}-${secLabel.replaceAll(" ", "_")}-${subIndex}-horizontal`}
                          className="bar bar-horizontal position-relative"
                          style={{
                            height: `${barSize}rem`,
                            flex: `0.01`,
                            background: "black",
                          }}
                        >
                          {legendDisplay}
                        </div>
                      );
                    }

                    const borderRadius = calculateBarBorders(subIndex, bar);
                    const isLastSection = subIndex === bar.data.length - 1;

                    return (
                      <div
                        key={`${tick}-${secLabel.replaceAll(" ", "_")}-${subIndex}-horizontal`}
                        className="bar bar-horizontal position-relative"
                        style={{
                          height: `${barSize}rem`,
                          flex: `${percentFloatValue}`,
                          background: secColor.includes("#FF595E")
                            ? `repeating-linear-gradient(-45deg, ${secColor}, ${secColor} 6px, #F14668 6px, #F14668 7px)`
                            : secColor,
                          borderRadius,
                          position: "relative",
                          ...(isLastSection && {
                            boxShadow: "2px 0 0 0 black",
                          }),
                        }}
                      >
                        {legendDisplay}
                      </div>
                    );
                  })}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default TutorBarGraph;
